import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
} from '@mui/material';
import { Healing as HealingIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Edit as EditIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { MedicalRecord } from '../../utils/api';
import { Procedure } from '../../services/procedureService';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';

interface ProcedureCardProps {
  record: MedicalRecord | Procedure;
  onEdit?: (procedure: Procedure) => void;
  onMenu?: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  canEdit?: boolean;
}

const ProcedureCard: React.FC<ProcedureCardProps> = ({ record, onEdit, onMenu, canEdit = false }) => {
  const [expanded, setExpanded] = useState(false);
  // The date line renders as an INSTANT in the CLINIC zone.
  //
  // DATE-KINDS §1.2 lists BOTH shapes this card accepts as MIXED, and §2.4
  // resolves the site "by which record type the card got" — so the kind was
  // traced per writer rather than assumed:
  //   · `Procedure.date` — ProcedureDialog's **DateTimePicker** posts a real
  //     moment (`procedures.service.ts:31` `new Date(dto.date)`). INSTANT.
  //   · legacy `MedicalRecord.date` — this card's `notes` back-parser only ever
  //     matches rows the ORIGINAL procedure dialog wrote (commit d1c91994:
  //     `DateTimePicker` -> `date: data.date.toISOString()`). INSTANT.
  //     The `type === 'procedure'` arm the callers test is NOT a MedicalRecord
  //     discriminator: `HorseProfileClassic.tsx:800-806` and
  //     `HorseProfileModern.tsx:825-831` SPREAD `Procedure` rows and stamp
  //     `type: 'procedure'` onto them for the combined timeline, so that arm is
  //     the Procedure case again. No writer in `frontend/src` or `backend/src`
  //     persists a `MedicalRecord` with `type: 'procedure'`, and the
  //     `type="date"` MedicalRecord writers (`LabsDiagnosticsPanel` ->
  //     imaging/outside_lab/diagnostic) never reach this card.
  //   · barn-ops bulk billing also creates `Procedure` rows
  //     (`billing.service.ts:3198` `serviceDate = new Date(dto.date)`) from a
  //     picked DAY. Both writers now post that day at **noon UTC**
  //     (`ymdToNoonUtcIso`) — `Billing/BarnOperations.tsx:493` and, since this
  //     branch, `Barns/BarnVisitWorkspace.tsx` — which reads back as the
  //     correct day, at a meaningless 7/8 AM, in every US clinic zone.
  //
  // ⚠ Residual, and HISTORICAL only: barn-ops rows written before those two
  // fixes carry a UTC-MIDNIGHT anchor and read as the previous evening in a US
  // zone. They carry no discriminator on the wire, and inferring the KIND from
  // the stored anchor — "midnight means a day" — is precisely what DATE-KINDS
  // §0 forbids (the kind comes from the WRITE path, never from how the value
  // reads), so this render deliberately does not guess. Closing it is a
  // backfill, not a render change.
  const formatInstant = useFormatInstant();
  
  // Check if this is a Procedure object (has 'name' field) or old MedicalRecord format
  const isProcedureObject = 'name' in record;
  
  let procedureName = 'Procedure';
  let template = '';
  let performedBy = '';
  let description = '';
  let notes = '';
  let findings = '';
  let procedureDate = '';

  if (isProcedureObject) {
    // New Procedure format - direct fields
    const proc = record as Procedure;
    procedureName = proc.name || 'Procedure';
    template = proc.category || '';
    performedBy = proc.performedBy || '';
    description = proc.description || '';
    notes = proc.notes || '';
    findings = proc.findings || '';
    procedureDate = proc.date;
  } else {
    // Old MedicalRecord format - parse notes
    const medRecord = record as MedicalRecord;
    procedureDate = medRecord.date;
    
    if (medRecord.notes) {
      const lines = medRecord.notes.split('\n');
      
      // Extract metadata from formatted notes
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.startsWith('PROCEDURE:')) {
          procedureName = line.replace('PROCEDURE:', '').trim();
        } else if (line.startsWith('TEMPLATE:')) {
          template = line.replace('TEMPLATE:', '').trim();
        } else if (line.startsWith('PERFORMED BY:')) {
          performedBy = line.replace('PERFORMED BY:', '').trim();
        } else if (line.trim() && !line.startsWith('PROCEDURE:') && !line.startsWith('TEMPLATE:') && !line.startsWith('PERFORMED BY:')) {
          // Collect all text as description
          if (!description) {
            description = line.trim();
          } else {
            description += '\n' + line.trim();
          }
        }
      }
    }
  }
  
  // Combine all text for display
  const allTextParts: string[] = [];
  if (description) allTextParts.push(description);
  if (findings) allTextParts.push(`Findings: ${findings}`);
  if (notes) allTextParts.push(`Notes: ${notes}`);
  
  const fullText = allTextParts.join('\n\n');
  const hasMoreContent = fullText.length > 200 || (description && findings) || (description && notes) || (findings && notes);
  const previewText = fullText.length > 200 ? fullText.substring(0, 200) + '...' : fullText;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <HealingIcon color="primary" />
            <Box flex={1}>
              <Typography variant="h6">
                {procedureName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <span data-testid="procedure-card-date">
                  {formatInstant(procedureDate, INSTANT_FORMATS.dateTime)}
                </span>
                {performedBy && ` • ${performedBy}`}
                {' • '}
                {/* formatDistanceToNow diffs two instants — zone-agnostic, correct as-is (DATE-KINDS §2.3). */}
                {formatDistanceToNow(new Date(procedureDate), { addSuffix: true })}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {template && (
              <Chip 
                label={template} 
                size="small" 
                variant="outlined"
              />
            )}
            {hasMoreContent && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ ml: 1 }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
            {onMenu && (
              <IconButton
                size="small"
                onClick={(e) => onMenu(e, record.id)}
                sx={{ ml: 1 }}
                title="More options"
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {fullText && (
          <Box sx={{ mt: 1 }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                fontStyle: 'italic',
                borderLeft: '3px solid',
                borderColor: 'primary.main',
                pl: 2,
                py: 0.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: expanded ? 'none' : 'none',
                overflow: expanded ? 'visible' : 'visible'
              }}
            >
              {expanded ? fullText : previewText}
            </Typography>
          </Box>
        )}

        {canEdit && isProcedureObject && onEdit && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onEdit(record as Procedure)}
            >
              Edit Procedure
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProcedureCard;
